import LinkIcon from '@mui/icons-material/Link'
import { Box, Button, Link, Stack } from '@mui/material'
import ResumeDownloadButton from './ResumeDownloadButton'
import { type GridColDef } from '@mui/x-data-grid-pro'
import { SortIndexField } from '__generated__/graphql'
import { formatDate } from 'date-fns'
import {
    outreachStatusIcons,
    recruitingStatusIcons,
} from 'shared/components/candidateStatusIcons'
import CandidateCell from 'recruiter/pages/Research/CustomCells/CandidateCell'
import { recruiterRouteConfig } from 'recruiter/routes'
import { type RosterCandidateTableRowModel } from 'recruiter/contexts/CandidateSqlSearchContext'
import { isCandidateRecruitingStatus } from 'shared/models/candidateRecruitingStatus'
import { getFullName, getInitials } from 'shared/utils'

export type CandidateTableRowModel = RosterCandidateTableRowModel

export type CandidateTableColumnDefinition =
    GridColDef<CandidateTableRowModel> & {
        sortIndexField?: SortIndexField
    }

const valueGetter = (value: unknown) => value ?? '-'

export const candidateTableColumns: CandidateTableColumnDefinition[] = [
    {
        field: 'candidate',
        headerName: 'Candidate',
        renderCell: ({ row }) => {
            return (
                <CandidateCell
                    profileImgKey={
                        row?.profileImageKey || row?.internalProfileImageKey
                    }
                    candidateId={row.candidateId ?? ''}
                    initials={getInitials({
                        firstName: row.firstName,
                        nickname: row.nickname,
                        lastName: row.lastName,
                    })}
                    name={getFullName({
                        firstName: row.firstName,
                        nickname: row.nickname,
                        lastName: row.lastName,
                    })}
                    title={row?.currentTitle ?? ''}
                    starRating={row?.starRatingAvg}
                />
            )
        },
        sortable: false,
        width: 200,
    },
    {
        field: 'status',
        headerName: 'Status',
        sortable: false,
        width: 75,
        renderCell: ({ row }) => {
            const recStatus =
                row.recruitingStatus &&
                isCandidateRecruitingStatus(row.recruitingStatus)
                    ? row.recruitingStatus
                    : 'No Status'
            const RecIcon =
                recruitingStatusIcons[recStatus] ??
                recruitingStatusIcons['No Status']
            const outStatus = row?.outreachStatus ?? 'No Status'
            const OutIcon =
                outreachStatusIcons[outStatus] ??
                outreachStatusIcons['No Status']
            return (
                <Stack direction="row" alignItems="center" gap={0.5}>
                    <RecIcon />
                    <OutIcon />
                </Stack>
            )
        },
    },
    {
        field: 'company',
        headerName: 'Company',
        editable: false,
        sortable: true,
        sortIndexField: SortIndexField.Company,
        width: 150,
        renderHeader: () => (
            <span
                data-testid="column-header-company"
                style={{ fontWeight: 'var(--unstable_DataGrid-headWeight)' }}
            >
                Company
            </span>
        ),
        renderCell: ({ row }) =>
            row.currentCompany ? (
                <Link
                    href={`/companies/${row.currentCompanyId}`}
                    sx={{ textDecoration: 'none' }}
                >
                    {row.currentCompany}
                </Link>
            ) : (
                '-'
            ),
    },
    {
        field: 'currentTitle',
        headerName: 'Current Title',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.CurrentTitle,
    },
    {
        field: 'industries',
        headerName: 'Industries',
        editable: false,
        sortable: true,
        sortIndexField: SortIndexField.CurrentIndustry,
        width: 150,
        renderCell: ({ row }) => {
            const industryString =
                row.currentIndustry?.split(';').join(', ') || '-'

            return (
                <Box component="span" title={industryString}>
                    {industryString}
                </Box>
            )
        },
    },
    {
        field: 'sectors',
        headerName: 'Sectors',
        editable: false,
        sortable: true,
        sortIndexField: SortIndexField.CurrentSector,
        width: 150,
        renderCell: ({ row }) => {
            const sectorString = row.currentSector?.split(';').join(', ') || '-'

            return (
                <Box component="span" title={sectorString}>
                    {sectorString}
                </Box>
            )
        },
    },
    {
        field: 'currentSectorDescription',
        headerName: 'Sector Description',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.SectorDescription,
    },
    {
        field: 'currentLocation',
        headerName: 'Current Location',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.CurrentLocation,
    },
    {
        field: 'startDate',
        headerName: 'Start Date',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.StartDate,
    },
    {
        field: 'previousCompany',
        headerName: 'Previous Company',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.PreviousCompany,
    },
    {
        field: 'previousIndustry',
        headerName: 'Previous Industry',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.PreviousIndustry,
    },
    {
        field: 'previousSector',
        headerName: 'Previous Sector',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.PreviousSector,
    },
    {
        field: 'gender',
        headerName: 'Gender',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.Gender,
    },
    {
        field: 'ethnicity',
        headerName: 'Ethnicity',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.Ethnicity,
    },
    {
        field: 'visaStatus',
        headerName: 'Visa Status',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.VisaStatus,
    },
    {
        field: 'cpiPlaced',
        headerName: 'CPI Placed',
        valueGetter: (_value, row) => row?.cpiAttribute ?? '-',
        sortable: true,
        sortIndexField: SortIndexField.CpiAttribute,
    },
    {
        field: 'undergradCollege',
        headerName: 'Undergrad College',
        editable: false,
        sortable: true,
        sortIndexField: SortIndexField.UndergradCollege,
        width: 150,
        valueGetter: (_value, row) => row.undergradCollege || '-',
    },
    {
        field: 'graduateCollege',
        headerName: 'Graduate College',
        editable: false,
        sortable: true,
        sortIndexField: SortIndexField.GraduateCollege,
        width: 150,
        valueGetter: (_value, row) => row.graduateCollege || '-',
    },
    {
        field: 'undergradYear',
        headerName: 'Undergrad Year',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.UndergradYear,
    },
    {
        field: 'undergradGpa',
        headerName: 'Undergrad GPA',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.UndergradGpa,
    },
    {
        field: 'sat',
        headerName: 'SAT',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.Sat,
    },
    {
        field: 'act',
        headerName: 'ACT',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.Act,
    },
    {
        field: 'gmat',
        headerName: 'GMAT',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.Gmat,
    },
    {
        field: 'hometown',
        headerName: 'Hometown',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.Hometown,
    },
    {
        field: 'analystClassYear',
        headerName: 'Analyst Class Year',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.AnalystClassYear,
    },
    {
        field: 'recruitingIndustryPref1',
        headerName: 'Recruiting Industry Pref 1',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.RecruitingIndustryPref_1,
    },
    {
        field: 'recruitingIndustryPref2',
        headerName: 'Recruiting Industry Pref 2',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.RecruitingIndustryPref_2,
    },
    {
        field: 'recruitingSectorPref1',
        headerName: 'Recruiting Sector Pref 1',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.RecruitingSectorPref_1,
    },
    {
        field: 'recruitingSectorPref2',
        headerName: 'Recruiting Sector Pref 2',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.RecruitingSectorPref_2,
    },
    {
        field: 'recruitingGeoPref1',
        headerName: 'Recruiting Geo Pref 1',
        valueGetter: (_value, row) => row.recruitingGeoLocationPref1 || '-',
        sortable: true,
        sortIndexField: SortIndexField.RecruitingGeoPref_1,
    },
    {
        field: 'recruitingGeoPref2',
        headerName: 'Recruiting Geo Pref 2',
        valueGetter: (_value, row) => row.recruitingGeoLocationPref2 || '-',
        sortable: true,
        sortIndexField: SortIndexField.RecruitingGeoPref_2,
    },
    {
        field: 'starRatingAvg',
        headerName: 'Star Rating Avg',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.StarRating,
    },
    {
        field: 'starRatingMostRecentScreen',
        headerName: 'Star Rating Most Recent Screen',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.StarRatingMostRecentScreen,
    },
    {
        field: 'starRatingReference',
        headerName: 'Star Rating Reference',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.StarRatingReference,
    },
    {
        field: 'referenceAttribute',
        headerName: 'Reference Attribute',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.ReferenceAttribute,
    },
    {
        field: 'preAdmitAttribute',
        headerName: 'Pre-Admit Attribute',
        valueGetter: (_value, row) => (row?.preAdmitAttribute ? 'Yes' : 'No'),
        sortable: true,
        sortIndexField: SortIndexField.PreAdmitAttribute,
    },
    {
        field: 'email',
        headerName: 'Email',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.Email,
    },
    {
        field: 'preferredEmail',
        headerName: 'Preferred Email',
        valueGetter,
        sortable: true,
        sortIndexField: SortIndexField.PreferredEmail,
    },
    {
        field: 'phone',
        headerName: 'Phone',
        valueGetter,
        sortable: false,
    },
    {
        field: 'candidateDocumentId',
        headerName: 'Resume',
        renderCell: ({ row }) =>
            row.candidateDocumentId ? (
                <ResumeDownloadButton documentId={row.candidateDocumentId} />
            ) : (
                '-'
            ),
        sortable: false,
    },
    {
        field: 'resumeUpdatedDate',
        headerName: 'Resume Updated',
        valueGetter: (value) =>
            value ? formatDate(new Date(value as string), 'MM/dd/yyyy') : '-',
        sortable: true,
        sortIndexField: SortIndexField.ResumeUpdated,
    },
    {
        field: 'linkedinUrl',
        headerName: 'LinkedIn URL',
        renderCell: ({ row }) =>
            row.linkedinUrl ? (
                <Link href={`//${row.linkedinUrl}`}>{row.linkedinUrl}</Link>
            ) : (
                '-'
            ),
        sortable: false,
    },
    {
        field: 'candidateProfileUrl',
        headerName: 'Profile',
        renderCell: ({ row }) => (
            <Button
                href={`${recruiterRouteConfig.candidates.path}/${row.candidateId}`}
                aria-label="View Candidate Profile"
                variant="text"
                size="small"
            >
                <LinkIcon />
            </Button>
        ),
        sortable: false,
    },
]
